<template>
  <div class="ma-4">
    <v-toolbar>
      <v-menu bottom>
        <template v-slot:activator="{ on }">
          <v-btn text v-on="on">
            Capstone Course Evaluations
            <v-icon right>fal fa-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item to="/student/capstone/admin">
            <v-list-item-title>Capstone Course Evaluation Overview</v-list-item-title>
          </v-list-item>
          <v-list-item v-if="isAdmin" to="/student/capstone/admin/students">
            <v-list-item-title>Capstone Course Students</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <v-spacer />
      <v-menu v-if="terms.length > 1">
        <template v-slot:activator="{ on }">
          <v-btn v-on="on">
            {{ termLabel }}
            <v-icon right>fal fa-chevron-down</v-icon>
          </v-btn>
        </template>
        <v-list>
          <v-list-item v-for="{ text, value } in terms" :key="value" @click="term = value">
            <v-list-item-title>{{ text }}</v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
      <span v-else>{{ termLabel }}</span>
    </v-toolbar>
    <v-toolbar v-if="isAdmin || (isDean && deanOpen) || courses.length > 1" class="mb-4">
      <v-select v-if="isAdmin" v-model="areaFilter" label="Area" :items="areas" item-text="name" item-value="code" outlined hide-details dense>
        <template v-slot:item="{ item }">
          <v-list-item-content>
            <v-list-item-title>{{ item.name }}</v-list-item-title>
            <v-list-item-subtitle>{{ item.subjects.join(', ') }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-select>
      <v-autocomplete v-if="courses.length > 1" v-model="courseFilter" label="Course" :items="courses" outlined dense multiple chips small-chips deletable-chips hide-details class="mx-2">
        <template v-slot:item="{ item }">
          <v-list-item-action>
            <v-icon>{{ courseFilter.includes(item.value) ? 'fas fa-check-square' : 'fal fa-square' }}</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{ item.text }}</v-list-item-title>
            <v-list-item-subtitle>{{ item.count }} Submission{{ item.count === 1 ? '' : 's' }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
      <v-autocomplete v-if="isAdmin || (isDean && deanOpen)" v-model="superFilter" label="Supervisor" :items="supervisors" outlined dense multiple chips small-chips deletable-chips hide-details class="mx-2">
        <template v-slot:item="{ item }">
          <v-list-item-action>
            <v-icon>{{ superFilter.includes(item.value) ? 'fas fa-check-square' : 'fal fa-square' }}</v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{ item.text }}</v-list-item-title>
            <v-list-item-subtitle>{{ item.count }} Submission{{ item.count === 1 ? '' : 's' }}</v-list-item-subtitle>
          </v-list-item-content>
        </template>
      </v-autocomplete>
    </v-toolbar>
    <v-card v-for="{ _id, createdAt, course, supervisor, feedback } of evals" :key="_id" class="mb-4 pt-4">
      <v-card-text>
        <v-toolbar flat>
          <v-text-field :value="stringFormatDate(createdAt)" label="Submitted Date" readonly hide-details style="max-width:250px" />
          <v-spacer />
          <v-text-field :value="course" label="Course" readonly hide-details style="max-width:150px" />
          <v-spacer />
          <v-text-field :value="supervisor" label="Capstone Supervisor" readonly hide-details style="max-width:250px" />
        </v-toolbar>
      </v-card-text>
      <v-card-text v-html="feedback" />
    </v-card>
  </div>
</template>
<script>
import { computed, onMounted, ref, watch } from '@vue/composition-api'
import { stringFormatDate } from '@/helpers/formatters'
import { courseList, deans } from '@/components/student/capstone/courses'

export default {
  setup (props, { root }) {
    const user = computed(() => root.$store.state.user.spoof || root.$store.state.user)
    const isAdmin = computed(() => user.value.roles.includes('Technology Services') || user.value.roles.includes('Institutional Research'))
    const deanOpen = ref(false)
    const isDean = computed(() => {
      const rec = deans.find(({ deans }) => deans ? deans.find(({ pidm }) => pidm === user.value.pidm) : false)
      if (rec) return true
      return false
    })
    const areas = ref([
      { name: 'All Courses', code: 'ALL', subjects: [] },
      { name: 'Arts and Letters', code: 'ART', subjects: ['ART', 'ENG', 'MUS', 'THT'], deans: [{ pidm: '47752', name: 'Bill Tate' }] },
      { name: 'Humanities', code: 'HUM', subjects: ['BIB', 'EDU', 'FRE', 'HIS', 'IDS', 'PHI', 'SPA'], deans: [{ pidm: '101942', name: 'John Wingard' }, { pidm: '371', name: 'Sarah Donaldson' }] },
      { name: 'Social Sciences', code: 'SOC', subjects: ['BUS', 'CDV', 'ECO', 'POL', 'INS', 'SOC', 'SPM'], deans: [{ pidm: '2254', name: 'Cale Horne' }] },
      { name: 'Sciences', code: 'SCI', subjects: ['BIO', 'CHE', 'COS', 'MAT', 'PHY', 'PSY'], deans: [{ pidm: '1000299', name: 'Tim Morris' }] }
    ])
    const terms = ref([])
    const term = computed({
      get: () => root.$store.state.capstone.term,
      set: (val) => root.$store.commit('capstone/setTerm', val)
    })
    const termLabel = computed(() => {
      const rec = terms.value.find(({ value }) => value === term.value)
      return rec ? rec.text : term.value
    })
    const openDate = ref('')
    const closedDate = ref('')
    const evalAdminOpen = ref(false)
    const courses = computed({
      get: () => root.$store.state.capstone.courses,
      set: (val) => root.$store.commit('capstone/setCourses', val)
    })
    const supervisors = computed({
      get: () => root.$store.state.capstone.supervisors,
      set: (val) => root.$store.commit('capstone/setSupervisors', val)
    })

    const areaFilter = computed({
      get: () => root.$store.state.capstone.filter.area,
      set: (area) => root.$store.commit('capstone/setFilter', { area })
    })
    const courseFilter = computed({
      get: () => root.$store.state.capstone.filter.course,
      set: (course) => root.$store.commit('capstone/setFilter', { course })
    })
    const superFilter = computed({
      get: () => root.$store.state.capstone.filter.supervisor,
      set: (supervisor) => root.$store.commit('capstone/setFilter', { supervisor })
    })
    watch(areaFilter, () => {
      if (areaFilter.value === 'ALL') courseFilter.value = []
      else {
        const area = areas.value.find(({ code }) => code === areaFilter.value)
        if (area) {
          courseFilter.value = courses.value.filter(({ value }) => area.subjects.includes(value.substring(0, 3))).map(({ value }) => value)
        }
      }
    })

    const evals = ref([])

    onMounted(async () => {
      const query = { type: 'Traditional', start: { $lte: new Date() }, capstoneCourseEvals: { $exists: true }, $sort: { start: -1 }, $limit: 10 }
      const { data } = await root.$feathers.service('system/term').find({ query })
      terms.value = data.map(({ label: text, term: value }) => { return { text, value } })
      if (term.value === '') {
        term.value = terms.value[0].value
      }
    })

    watch(term, async () => {
      if (term.value !== '') {
        const { data } = await root.$feathers.service('system/term').find({ query: { term: term.value } })
        if (data.length === 1 && 'capstoneCourseEvals' in data[0]) {
          openDate.value = data[0].capstoneCourseEvals.open
          closedDate.value = data[0].capstoneCourseEvals.close
          deanOpen.value = (new Date(data[0].capstoneCourseEvals.openToDeans) < new Date())
          const instrOpen = (new Date(data[0].capstoneCourseEvals.openToInstructors) < new Date())
          if (!isAdmin.value && !instrOpen) {
            root.$store.dispatch('main/snackbar', { color: 'error', text: 'You do not have permission to access the requested page at this time.' })
            root.$router.replace('/student/capstone/admin')
            return
          }
          await loadGroupings()
          await loadData()
        } else {
          alert('There was an error looking up the capstone information for the current term.')
        }
      }
    })

    async function loadGroupings () {
      // Load the distinct list of courses - or should we just use the capstone courseList??
      const aggregate = [
        { $match: { term: term.value } },
        { $group: { _id: '$course', count: { $sum: 1 } } },
        { $project: { text: '$_id', value: '$_id', count: 1 } },
        { $sort: { text: 1 } }
      ]
      if (isDean.value && deanOpen.value) {
        const rec = deans.find(({ deans }) => deans ? deans.find(({ pidm }) => pidm === user.value.pidm) : false)
        aggregate[0].$match.course.$in = courseList.filter((course) => rec.subjects.includes(course.substring(0, 3)))
      } else if (!isAdmin.value) {
        aggregate[0].$match['supervisor'] = user.value.preferred + ' ' + user.value.last
      }
      const { data: courseData } = await root.$feathers.service('student/capstone-eval').find({ query: { aggregate } })
      courses.value = courseData
      // Load the distinct list of supervisors for submitted evals
      aggregate[1].$group._id = '$supervisor'
      const { data: superData } = await root.$feathers.service('student/capstone-eval').find({ query: { aggregate } })
      console.log(superData)
      supervisors.value = superData
    }

    watch([courseFilter, superFilter], () => {
      loadData()
    })

    async function loadData () {
      const $match = { term: term.value }
      if (courseFilter.value.length > 0 || superFilter.value.length > 0) {
        if (courseFilter.value.length > 0) {
          $match.course = { $in: courseFilter.value }
        }
        if (superFilter.value.length > 0) {
          $match.supervisor = { $in: superFilter.value }
        }
      } else if (isDean.value && deanOpen.value) {
        // Limit to only the subjects within the dean's area
        const rec = deans.find(({ deans }) => deans ? deans.find(({ pidm }) => pidm === user.value.pidm) : false)
        $match.course.$in = courseList.filter((course) => rec.subjects.includes(course.substring(0, 3)))
      } else if (!isAdmin.value) {
        // Limit only to their own courses
        $match['supervisor'] = user.value.preferred + ' ' + user.value.last
      }
      const aggregate = [{ $match }]
      aggregate.push({ $sort: { createdAt: 1 } })
      const { data } = await root.$feathers.service('student/capstone-eval').find({ query: { aggregate } })
      evals.value = data
    }

    return {
      user,
      isAdmin,
      isDean,
      deanOpen,
      areas,
      terms,
      term,
      termLabel,
      openDate,
      closedDate,
      evalAdminOpen,
      courses,
      supervisors,
      areaFilter,
      courseFilter,
      superFilter,
      evals,
      stringFormatDate
    }
  }
}
</script>
